/**
 * The records a field names (`cis_field(ref=...)`), as record facts, child rows and list cells link them. Pure: the
 * pages render what this returns.
 */
import type { ManifestColumn, ObjectId, ObjectRef } from "./contract";
import type { ManifestIndex } from "./manifest";

/** A record another record names, as it links: the value people read, the page it opens, and the record itself (both
 *  null where the value names no record that has a page). */
export interface PresentedRef {
  label: string;
  href: string | null;
  record: ObjectRef | null;
}

const compiled = new Map<string, RegExp>();
function regexOf(pattern: string): RegExp {
  let regex = compiled.get(pattern);
  if (!regex) {
    regex = new RegExp(pattern);
    compiled.set(pattern, regex);
  }
  return regex;
}

/** The value at `path` (a field name, or dotted names into nested objects: `supplier.supplier_no`) in a record or row. */
function valueAt(row: Record<string, unknown>, path: string): unknown {
  let node: unknown = row;
  for (const part of path.split(".")) {
    if (node === null || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

/** The text and numbers a value holds, one or a list; an empty string or null keeps its place (null), so a list read
 *  beside another (a label and the number or object beside it) stays in step. */
function slotsOf(value: unknown): (string | number | null)[] {
  return (Array.isArray(value) ? value : [value]).map((v) =>
    (typeof v === "string" && v !== "") || typeof v === "number" ? v : null,
  );
}

/**
 * The record `id` opens among `targets`: the first object whose line pattern or number pattern it matches (a line
 * reference opens the record it is a line of). An object whose numbers have no pattern takes any value only when it is
 * the lone target. Null where the first match has no record page, or none matches (a staging reference in a column
 * that mostly holds order lines), so a value never links to a 404.
 */
function recordFor(
  index: ManifestIndex,
  targets: readonly ObjectId[],
  id: string,
): ObjectRef | null {
  for (const target of targets) {
    const named = index.objectOf(target);
    const { pattern, line_pattern } = named.identifier;
    const isLine = !!line_pattern && regexOf(line_pattern).test(id);
    const isNumber = pattern ? regexOf(pattern).test(id) : targets.length === 1;
    if (!isLine && !isNumber) continue;
    if (!named.record) return null;
    return { object: target, id: isLine ? id.replace(/\.[0-9]+$/, "") : id };
  }
  return null;
}

/**
 * The records a `ref` field names, each with its page: one value or a list of them, read from `row` (a record, or a
 * child or list row). A field with `ref_id_field` shows its own value (a name) and links by the number found at that
 * field (a customer name by the customer code beside it); with no number the name shows as text. A field with
 * `ref_object_field` links each value to the object found at that field, value for value (a purchase invoice, which
 * has no number, among the objects a tag may be). Null for a field that declares no `ref` (or no object this manifest
 * carries), and for a value that holds nothing.
 */
export function refsOf(
  index: ManifestIndex,
  column: ManifestColumn,
  row: Record<string, unknown>,
): PresentedRef[] | null {
  if (!column.ref) return null;
  const targets = (
    Array.isArray(column.ref) ? column.ref : [column.ref]
  ).filter((target) => index.isObjectKnown(target));
  if (targets.length === 0) return null;
  const labels = slotsOf(row[column.field]);
  if (labels.every((label) => label === null)) return null;
  const ids = column.ref_id_field
    ? slotsOf(valueAt(row, column.ref_id_field))
    : labels;
  const objects = column.ref_object_field
    ? slotsOf(valueAt(row, column.ref_object_field))
    : null;
  const recordAt = (i: number): ObjectRef | null => {
    const id = ids[i];
    if (id === null || id === undefined) return null;
    if (!objects) return recordFor(index, targets, String(id));
    const object = objects[i];
    return typeof object === "string" && targets.includes(object)
      ? recordFor(index, [object], String(id))
      : null;
  };
  return labels.flatMap((value, i) => {
    if (value === null) return [];
    const record = recordAt(i);
    return [
      {
        label: String(value),
        href: record ? index.hrefOf(record) : null,
        record,
      },
    ];
  });
}

/**
 * The record a bare CIS number names (ORD-K48193, PO-K20417; a line reference opens the record it is a line of),
 * whichever object it belongs to: the first object with a record page whose number pattern it matches. For a value
 * shown without a field that says what it is (a test run's made numbers). Null where nothing matches, so the value
 * shows as text and never links to a 404.
 */
export function recordOfNumber(
  index: ManifestIndex,
  value: string,
): ObjectRef | null {
  const targets = index.manifest.objects
    .filter((object) => object.record && object.identifier.pattern)
    .map((object) => object.id);
  return recordFor(index, targets, value);
}
